import { useCallback, useEffect, useRef, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import pageCatalog from '@/i18n/ns/connections-page';
import { useCurrentConnectionRequest } from '../connect-access/use-current-request';
import { customMcpApi, type CustomMcpConnection } from './api';
import { errorMessage } from './presentation';
import { useAppLocale } from '@/i18n/context';
import { CustomMcpDialog } from './dialog';
import type { ConnectionReadItem } from '../connection-read-access/api';
import { applyConnectChoice, pendingGrant, type PendingConnectChoice } from './connect-choice';

export type CustomMcpNotice = { text: string; tone: 'success' | 'error' };
type ServerDraft = { name?: string; url?: string };

/**
 * Connections?setup=mcp-server&name=…&url=… (an outside agent's connect_in_poneva link) opens Add
 * server with its name and public address filled in. Never a sign-in: keys are entered in the form.
 */
export function serverSetupRequested(): ServerDraft | null {
  const query = new URLSearchParams(location.hash.split('?')[1] ?? '');
  if (query.get('setup') !== 'mcp-server') return null;
  return { name: query.get('name') ?? undefined, url: query.get('url') ?? undefined };
}

/** A setup link opens Add server once this person may add a server, as Add itself does. */
function useServerSetupLink(canConnect: boolean, openDraft: (draft: ServerDraft) => void) {
  const [requested, setRequested] = useState<ServerDraft | null>(serverSetupRequested);
  useEffect(() => {
    if (!requested || !canConnect) return;
    setRequested(null);
    openDraft(requested);
  }, [requested, canConnect, openDraft]);
  // A second link while the page is open fills the form in again.
  useEffect(() => {
    const onHash = () => {
      const draft = serverSetupRequested();
      if (draft) setRequested(draft);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
}

/**
 * The person's own MCP servers (custom connections and Shopify Developer App stores): the list,
 * sign-in, test and removal, and the Add server dialog. The Connections page shows them as rows of
 * Your connections; the host keys this hook's component by account and company, so a switch starts
 * over and an earlier context's late response is never applied.
 */
export function useCustomMcp({
  managedConnectionIds = [],
  refresh: refreshRequest,
  onChanged,
}: {
  managedConnectionIds?: string[];
  /** A new version re-reads the list, e.g. after the catalogue added a Shopify store. */
  refresh?: { version: number; notice?: string };
  /** A connection was removed or re-tested here, so the catalogue's cards re-read their state. */
  onChanged?: () => void;
}) {
  const locale = useAppLocale();
  const request = useCurrentConnectionRequest();
  const copy = useNamespaceCopy(pageCatalog);
  const [problems, setProblems] = useState<Record<string, { binding: string; text: string }>>({});
  const readOrder = useRef(0);
  const [data, setData] = useState<{
    items: CustomMcpConnection[];
    canConnect: boolean;
    canConnectCompany: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState<ServerDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<CustomMcpNotice | null>(null);
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null);
  const mounted = {
    get current() {
      return request.isMounted();
    },
  };
  // The connect-time reading choice for the server just created, until its own grant appears.
  const pending = useRef<PendingConnectChoice | null>(null);

  function setReadAccess(read: ConnectionReadItem) {
    if (!mounted.current) return;
    setData((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) =>
              item.id === read.connectionId ? { ...item, readAccess: read } : item,
            ),
          }
        : current,
    );
  }

  async function applyPending(items: CustomMcpConnection[]) {
    const choice = pending.current;
    const read = pendingGrant(items, choice);
    if (!choice || !read) return;
    pending.current = null;
    const saved = await applyConnectChoice(read, choice.choice, request.fetch);
    if (saved && mounted.current) setReadAccess(saved);
  }

  const refresh = async () => {
    const order = ++readOrder.current;
    try {
      setLoadError(null);
      const response = await customMcpApi.list(request.fetch);
      if (!mounted.current || order !== readOrder.current) return;
      setData({
        ...response,
        items: Array.isArray(response.items) ? response.items : [],
        canConnect: response.canConnect === true,
        canConnectCompany: response.canConnectCompany === true,
      });
      if (Array.isArray(response.items)) await applyPending(response.items);
      return response;
    } catch (error) {
      if (!mounted.current || order !== readOrder.current) return;
      setLoadError(errorMessage(error, locale));
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  // A store connected from the integrations catalogue lands here, its access already applied.
  const requested = useRef(refreshRequest?.version ?? 0);
  useEffect(() => {
    if (!refreshRequest || refreshRequest.version === requested.current) return;
    requested.current = refreshRequest.version;
    if (refreshRequest.notice) setNotice({ text: refreshRequest.notice, tone: 'success' });
    void refresh();
  }, [refreshRequest]);

  function refreshAfterAuthorization(id: string, attempt = 0) {
    if (attempt >= 60) return;
    window.setTimeout(() => {
      if (!mounted.current) return;
      void refresh().then((response) => {
        if (response?.items.some((item) => item.id === id && item.status === 'connected')) {
          setAuthorizationUrl(null);
          setNotice({ text: copy.noticeAdded, tone: 'success' });
        } else refreshAfterAuthorization(id, attempt + 1);
      });
    }, 2000);
  }

  // Each draft is a new dialog, so a second link refills a form that is already open.
  const [draftId, setDraftId] = useState(0);
  const openDraft = useCallback((draft: ServerDraft) => {
    setDraftId((id) => id + 1);
    setOpen(draft);
  }, []);
  useServerSetupLink(data?.canConnect === true, openDraft);

  useEffect(() => {
    const result = new URLSearchParams(location.hash.split('?')[1]).get('customMcp');
    if (result === 'connected') setNotice({ text: copy.noticeAddedReturn, tone: 'success' });
    if (result === 'error') setNotice({ text: copy.noticeSignInUnfinished, tone: 'error' });
  }, []);

  async function run(id: string, operation: 'test' | 'authorize') {
    const owner = request.begin();
    if (!owner) return;
    const binding = data?.items.find((item) => item.id === id)?.readAccess?.bindingVersion;
    setBusy(id);
    setNotice(null);
    setAuthorizationUrl(null);
    let popup: Window | null = null;
    try {
      if (operation === 'test') {
        await customMcpApi.test(id, owner.fetch);
        if (!owner.current()) return;
        const latest = await refresh();
        if (!owner.current()) return;
        const current = latest?.items.find((item) => item.id === id);
        if (!binding || current?.readAccess?.bindingVersion !== binding) return;
        setProblems((previous) => {
          const next = { ...previous };
          delete next[id];
          return next;
        });
        setNotice({
          text: current.status === 'connected' ? copy.noticeChecked : copy.noticeUnreachable,
          tone: current.status === 'connected' ? 'success' : 'error',
        });
      } else {
        popup = window.open('', '_blank', 'width=680,height=760');
        const response = await customMcpApi.authorize(id, owner.fetch);
        if (!owner.current()) {
          popup?.close();
          return;
        }
        if (response.authorizationUrl) {
          if (popup) {
            popup.opener = null;
            popup.location.href = response.authorizationUrl;
          } else setAuthorizationUrl(response.authorizationUrl);
          refreshAfterAuthorization(id);
          setNotice({ text: copy.noticeSignInReady, tone: 'success' });
        } else {
          popup?.close();
          await refresh();
          setNotice({ text: copy.noticeAuthorized, tone: 'success' });
        }
      }
      if (operation !== 'test' && owner.current()) await refresh();
    } catch (error) {
      popup?.close();
      if (!owner.current()) return;
      const latest = await refresh();
      if (!owner.current()) return;
      const current = latest?.items.find((item) => item.id === id);
      if (!binding || current?.readAccess?.bindingVersion !== binding) return;
      // Said once, on the panel it belongs to.
      setProblems((previous) => ({
        ...previous,
        [id]: { binding, text: errorMessage(error, locale) },
      }));
    } finally {
      if (owner.finish()) setBusy(null);
      if (operation === 'test') onChanged?.();
    }
  }

  async function remove(id: string, fetchImpl?: typeof fetch) {
    await customMcpApi.remove(id, fetchImpl);
    if (mounted.current) await refresh();
    onChanged?.();
  }

  function openAuthorization(url: string) {
    const popup = window.open('', '_blank', 'width=680,height=760');
    if (popup) {
      popup.opener = null;
      popup.location.href = url;
    } else setAuthorizationUrl(url);
  }

  const items = data?.items.filter((item) => !managedConnectionIds.includes(item.id)) ?? [];
  const problemFor = (item: CustomMcpConnection) =>
    item.readAccess && problems[item.id]?.binding === item.readAccess.bindingVersion
      ? problems[item.id].text
      : null;
  return {
    loaded: data !== null,
    items,
    loadError,
    busy,
    notice,
    authorizationUrl,
    canConnect: data?.canConnect ?? false,
    canConnectCompany: data?.canConnectCompany ?? false,
    refresh,
    run,
    remove,
    setReadAccess,
    problemFor,
    openAdd: () => {
      setNotice(null);
      setOpen({});
    },
    dialog: open ? (
      <CustomMcpDialog
        key={draftId}
        canConnectCompany={data?.canConnectCompany ?? false}
        initial={open}
        onClose={() => setOpen(null)}
        onCreated={(response, popup, choice) => {
          setOpen(null);
          pending.current = { id: response.connection.id, choice };
          setAuthorizationUrl(null);
          if (response.authorizationUrl) {
            if (popup) {
              popup.opener = null;
              popup.location.href = response.authorizationUrl;
            } else openAuthorization(response.authorizationUrl);
            refreshAfterAuthorization(response.connection.id);
          }
          setNotice({
            text: response.authorizationUrl ? copy.noticeSignInReady : copy.noticeAdded,
            tone: 'success',
          });
          void refresh();
        }}
      />
    ) : null,
  };
}

export type CustomMcpState = ReturnType<typeof useCustomMcp>;
